// AdminDashboard.tsx (Clerk-es, letisztult változat)
//
// A hozzáférést az AdminProtectedRoute (App.tsx) ellenőrzi: csak az jut ide, akit a backend
// (admin_users tábla) adminnak ismer. Ezért itt nincs külön jogosultság-lekérdezés és átirányítás.

import React from "react";
import {
    Avatar,
    Box,
    Button,
    Container,
    Tab,
    Tabs,
    ThemeProvider,
    Typography,
    alpha,
    createTheme,
    responsiveFontSizes,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { FiArrowLeft, FiCalendar, FiDownload, FiShield } from "react-icons/fi";
import AdminLetolthetoTartalmak from "../components/AdminLetolthetoTartalmak";
import AdminKepzesIdopontok from "../components/AdminKepzesIdopontok";

const ACCENT = "#8ea2ff";
const BORDER = "rgba(255,255,255,0.08)";

let adminTheme = createTheme({
    palette: {
        mode: "dark",
        primary: { main: ACCENT },
        secondary: { main: "#f48fb1" },
        background: { default: "#0e1014", paper: "#161a22" },
        text: { primary: "#eef0f6", secondary: "#9aa3b5" },
        divider: BORDER,
    },
    shape: { borderRadius: 14 },
    typography: {
        fontFamily: 'Inter, "Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif',
        button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: "none", border: `1px solid ${BORDER}` },
            },
        },
        MuiButton: {
            styleOverrides: { root: { borderRadius: 10 } },
        },
        MuiTableCell: {
            styleOverrides: {
                root: { borderColor: BORDER },
                head: { color: "#9aa3b5", fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 0.4 },
            },
        },
    },
});
adminTheme = responsiveFontSizes(adminTheme);

type SectionKey = "tartalmak" | "idopontok";

const SECTIONS: {
    key: SectionKey;
    label: string;
    title: string;
    description: string;
    icon: React.ReactElement;
}[] = [
    {
        key: "tartalmak",
        label: "Letölthető tartalmak",
        title: "Letölthető tartalmak",
        description: "A weboldalon elérhető letölthető fájlok feltöltése, módosítása és törlése.",
        icon: <FiDownload size={17} />,
    },
    {
        key: "idopontok",
        label: "Képzés időpontok",
        title: "Képzés időpontok",
        description: "A következő képzés dátumai, helyszíne és a hozzá tartozó esemény-link.",
        icon: <FiCalendar size={17} />,
    },
];

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [searchParams, setSearchParams] = useSearchParams();

    // A fül az URL-ből olvasódik (?tab=idopontok); ismeretlen érték esetén az első fül nyílik meg
    const requested = searchParams.get("tab");
    const active = SECTIONS.find((s) => s.key === requested) ?? SECTIONS[0];

    const handleTabChange = (_e: React.SyntheticEvent, value: SectionKey) => {
        setSearchParams({ tab: value }, { replace: true });
    };

    const displayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || "Admin";

    return (
        <ThemeProvider theme={adminTheme}>
            <Box sx={{ bgcolor: "background.default", minHeight: "100vh", color: "text.primary" }}>
                {/* Felső sáv */}
                <Box
                    component="header"
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        borderBottom: `1px solid ${BORDER}`,
                        bgcolor: alpha("#0e1014", 0.8),
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 1.5 }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: "11px",
                                    display: "grid",
                                    placeItems: "center",
                                    color: "#0e1014",
                                    background: `linear-gradient(135deg, ${ACCENT}, #c4a3ff)`,
                                    flexShrink: 0,
                                }}
                            >
                                <FiShield size={19} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                                    Admin vezérlőpult
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Csillagtanoda
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
                                <Avatar src={user?.imageUrl} alt={displayName} sx={{ width: 32, height: 32 }} />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                    {displayName}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                startIcon={<FiArrowLeft />}
                                onClick={() => navigate("/")}
                                sx={{ borderColor: BORDER, whiteSpace: "nowrap" }}
                            >
                                Vissza az oldalra
                            </Button>
                        </Box>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
                    {/* Navigáció */}
                    <Tabs
                        value={active.key}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons={false}
                        TabIndicatorProps={{ sx: { display: "none" } }}
                        sx={{
                            minHeight: 0,
                            width: "fit-content",
                            maxWidth: "100%",
                            p: 0.5,
                            borderRadius: 999,
                            bgcolor: "background.paper",
                            border: `1px solid ${BORDER}`,
                            "& .MuiTab-root": {
                                minHeight: 40,
                                px: 2.5,
                                gap: 1,
                                borderRadius: 999,
                                color: "text.secondary",
                                transition: "background-color .15s, color .15s",
                            },
                            "& .MuiTab-root:hover": { color: "text.primary" },
                            "& .Mui-selected": {
                                color: `${ACCENT} !important`,
                                bgcolor: alpha(ACCENT, 0.16),
                            },
                        }}
                    >
                        {SECTIONS.map((s) => (
                            <Tab key={s.key} value={s.key} icon={s.icon} iconPosition="start" label={s.label} />
                        ))}
                    </Tabs>

                    {/* Szakasz címe */}
                    <Box sx={{ mt: 4, mb: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
                            {active.title}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                            {active.description}
                        </Typography>
                    </Box>

                    {/* Tartalom */}
                    {active.key === "tartalmak" && <AdminLetolthetoTartalmak />}
                    {active.key === "idopontok" && <AdminKepzesIdopontok />}
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default AdminDashboard;